/**
 * _AuthLayout.jsx
 * ─────────────────────────────────────────────────────────────────────
 * Tatalli Cloud — Auth Sayfaları Ortak Layout
 * Konum: src/Layouts/Shared/_AuthLayout.jsx
 *
 * Kullanım:
 *   import AuthLayout from '../../Layouts/Shared/_AuthLayout';
 *
 *   <AuthLayout
 *     titleKey="pages.login.title"
 *     leftHeadlineKey="auth.left.headline"
 *     leftSloganKey="header.slogan"
 *     leftDescKey="auth.left.description"
 *     leftFeatures={[
 *       { ikon: 'fa-shield-alt', key: 'auth.left.feature1' },
 *     ]}
 *   >
 *     {form içeriği}
 *   </AuthLayout>
 * ─────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/* CSS yolu: Layouts/Shared/ → Assets/Styles/ */
import '../../Assets/Styles/tatalli-cloud.css';

/* ─────────────────────────────────────────────────────────────────────
   AuthLayout
───────────────────────────────────────────────────────────────────── */
export default function AuthLayout({
  titleKey,
  leftHeadlineKey,
  leftSloganKey,
  leftDescKey,
  leftFeatures = [],
  children,
}) {
  const { t } = useTranslation();

  /* ── Tarayıcı sekmesi başlığı ── */
  useEffect(() => {
    if (titleKey) document.title = t(titleKey);
  }, [titleKey, t]);

  /*
   * Başlık tek satır:
   * JSON'da "Kurumsal Bulut\nAltyapısı" şeklinde \n var.
   * whiteSpace: 'normal' ile \n yok sayılır → tek satır kalır.
   * Birden fazla satır istenen sayfalar için leftHeadlineKey değerinde \n koyulmamalıdır.
   */
  const headline = t(leftHeadlineKey);

  return (
    <div className="auth-govde">

      {/* ── Mobil ek stiller ── */}
      <style>{`
        /* Emoji bayrak font — tüm tarayıcılarda görünür */
        .auth-dil-bayrak {
          font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji',
                       'Android Emoji', 'EmojiSymbols', sans-serif !important;
          font-size: 16px !important;
          line-height: 1 !important;
          display: inline-block !important;
          font-style: normal !important;
          font-weight: normal !important;
        }

        /* Varsayılan: mobil elementler gizli */
        .auth-mob-header { display: none; }
        .auth-mob-serit  { display: none; }

        @media (max-width: 768px) {
          /* Sağ panel: tam ekran, yukarıdan başla, scroll açık */
          .auth-sag-panel {
            width: 100% !important;
            min-height: 100dvh !important;
            padding: 0 !important;
            justify-content: flex-start !important;
            align-items: stretch !important;
            overflow-y: auto !important;
            box-shadow: none !important;
            background: #f8f9fc !important;
          }
          /* Form: tam genişlik */
          .auth-sag-panel .auth-form-konteyner {
            max-width: 100% !important;
            width: 100% !important;
            padding: 28px 20px 44px !important;
            box-sizing: border-box !important;
          }
          /* Web dil seçici satırı mobilde gizli */
          .auth-web-dil-satiri { display: none !important; }
          /* Mobil header: göster */
          .auth-mob-header {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            padding: 13px 20px;
            background: #001b33;
            flex-shrink: 0;
          }
          /* Marka şerit: göster */
          .auth-mob-serit {
            display: block !important;
            height: 3px;
            background: linear-gradient(90deg, #001324 0%, #013682 55%, #4db2ff 100%);
            flex-shrink: 0;
          }
          /* Başlık ve açıklama küçült */
          .auth-form-baslik   { font-size: 22px !important; }
          .auth-form-aciklama { font-size: 12px !important; margin-bottom: 22px !important; }
        }
      `}</style>

      {/* ── Sol Panel ── */}
      <div className="auth-sol-panel">
        <div className="auth-sol-panel-desen" />
        <div className="auth-sol-izgara" />
        <div className="auth-sol-icerik">
          <img src="/output-inverted.png" alt="Tatalli Cloud" className="auth-sol-logo" />

          {/* Başlık — \n içeriyorsa kaldır → tek satır */}
          <h1 className="auth-sol-baslik" style={{ whiteSpace: 'normal' }}>
            {headline.replace(/\\n|\n/g, ' ')}
          </h1>

          {leftSloganKey && (
            <p className="auth-sol-alt-baslik">{t(leftSloganKey)}</p>
          )}
          {leftDescKey && (
            <p className="auth-sol-aciklama">{t(leftDescKey)}</p>
          )}
          {leftFeatures.length > 0 && (
            <div className="auth-sol-ozellikler">
              {leftFeatures.map((f, i) => (
                <div className="auth-sol-ozellik-satiri" key={i}>
                  <div className="auth-sol-ozellik-ikon">
                    <i className={`fas ${f.ikon}`} />
                  </div>
                  <span>{t(f.key)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Sağ Panel ── */}
      <div className="auth-sag-panel">

        {/* Marka şerit — yalnızca mobilde görünür */}
        <div className="auth-mob-serit" />

        {/* Mobil header: logo + dil seçici */}
        <div className="auth-mob-header">
          <img
            src="/output-inverted.png"
            alt="Tatalli Cloud"
            style={{ height: 24, filter: 'contrast(0) brightness(2)', display: 'block' }}
          />
          <AuthDilSecici dark />
        </div>

        {/* Web: dil seçici sağ üstte — mobilde CSS ile gizlenir */}
        <div className="auth-web-dil-satiri" style={{
          width: '100%', maxWidth: 380,
          display: 'flex', justifyContent: 'flex-end',
          marginBottom: 8,
        }}>
          <AuthDilSecici />
        </div>

        {/* Sayfa form içeriği */}
        {children}

      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   AuthDilSecici
   dark=true  → koyu arka plan (mobil header)
   dark=false → açık arka plan (web sağ panel)
   .auth-dil-bayrak sınıfı emoji font'unu garantiler.
───────────────────────────────────────────────────────────────────── */
export function AuthDilSecici({ dark = false }) {
  const { i18n } = useTranslation();
  const [acik, setAcik] = useState(false);
  const ref = useRef(null);

  const DILLER = [
    { kod: 'en', ad: 'English', bayrak: '🇬🇧' },
    { kod: 'tr', ad: 'Türkçe',  bayrak: '🇹🇷' },
  ];
  const aktif = DILLER.find(d => d.kod === i18n.language) || DILLER[0];

  const degistir = (kod) => {
    i18n.changeLanguage(kod);
    try { localStorage.setItem('tatalli_dil', kod); } catch {}
    setAcik(false);
  };

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAcik(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const dugmeStil = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 20,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: 'inherit',
    lineHeight: 1,
    ...(dark
      ? {
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff',
        }
      : {
          background: '#f4f6f8',
          border: '1px solid #e2e8f0',
          color: '#374151',
        }
    ),
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button onClick={() => setAcik(p => !p)} style={dugmeStil}>
        <span className="auth-dil-bayrak">{aktif.bayrak}</span>
        <span>{aktif.kod.toUpperCase()}</span>
        <i className="fas fa-chevron-down" style={{ fontSize: 9 }} />
      </button>

      {acik && (
        <div className="dil-dropdown" style={{ right: 0, top: 'calc(100% + 6px)' }}>
          {DILLER.map(d => (
            <div
              key={d.kod}
              className={`dil-secenegi ${d.kod === aktif.kod ? 'dil-secenegi-aktif' : ''}`}
              onClick={() => degistir(d.kod)}
            >
              <span className="auth-dil-bayrak">{d.bayrak}</span>
              <span>{d.ad}</span>
              {d.kod === aktif.kod && (
                <i className="fas fa-check" style={{ marginLeft: 'auto', fontSize: 11 }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
